/**
 * This service manages the loading and processing of test questions.
 * Questions are loaded asynchronously to prevent blocking the main thread,
 * which appears to be the cause of initialization failures in the test environment.
 */

import questionsData from '../data/questions.json';
import { validateData } from './runtimeValidation.ts';

export interface Question {
  id: string;
  text: string;
  category?: string;
  primary_scale?: string;
  answers?: Array<string | { value?: string | number; text?: string }>;
  [key: string]: unknown;
}

let allQuestions: Question[] = [];
let initializationPromise: Promise<void> | null = null;
let rawQuestionsData: Question[] | null = null;

const processCategory = (category: string, questions: Question[]) => {
    if (Array.isArray(questions)) {
        (questions as Question[]).forEach((q: Question) => {
            allQuestions.push(q);
        });
    }
};

const shuffleArray = (array: Question[]): Question[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

/**
 * @typedef {Object} SessionQuestion
 * @property {string} id
 * @property {any} answer
 * @property {string} [text]
 */

/**
 * Initializes the questions by loading them from the JSON file.
 * This function is idempotent and can be called multiple times safely.
 * @returns {Promise<void>}
 */
export const initializeQuestions = (): Promise<void> => {
    if (!initializationPromise) {
        initializationPromise = new Promise<void>(async (resolve, reject) => {
            try {
                console.log('Initializing questions...');
                // --- Гарантируем массив вопросов ---
                const questionsArray = Array.isArray(questionsData.questions) ? questionsData.questions : (Array.isArray(questionsData) ? questionsData : []);
                console.log('[DEBUG][questionService] loaded questions:', questionsArray.map(q => q.id));
                rawQuestionsData = questionsArray; // Keep this as the array for consumers
                if (!questionsArray || !Array.isArray(questionsArray) || questionsArray.length === 0) {
                    console.error('Failed to load, or question data array is empty.', { data: questionsArray });
                    allQuestions = [];
                    resolve();
                    return;
                }
                console.log(`Loaded ${questionsArray.length} raw questions.`);
                allQuestions = [];
                processCategory('questions', questionsArray as Question[]);
                console.log(`Processed ${allQuestions.length} questions.`);
                if (allQuestions.length === 0) {
                    console.error('Question processing resulted in an empty list.');
                }
                try {
                    validateData('questions.schema.json', questionsArray);
                } catch (e) {
                    console.error('Ошибка валидации массива вопросов:', e.message);
                    allQuestions = [];
                    resolve();
                    return;
                }
                resolve();
            } catch (error) {
                console.error('Failed to load or process questions:', error);
                initializationPromise = null; // Allow retrying
                reject(error);
            }
        });
    }
    return initializationPromise;
};

/**
 * Returns the raw, unprocessed question data.
 * Must be called after initializeQuestions() has resolved.
 * @returns {Question[]}
 */
export const getRawQuestionsData = (): Question[] => {
    if (!rawQuestionsData) {
        console.warn("getRawQuestionsData called before questions were loaded.");
    }
    try {
        validateData('questions.schema.json', rawQuestionsData);
    } catch (e) {
        console.error('Ошибка валидации массива вопросов при получении:', e.message);
        return []; // Возвращаем пустой массив, если валидация не пройдена
    }
    return rawQuestionsData || [];
};

/**
 * Returns a shuffled list of all questions.
 * Must be called after initializeQuestions() has resolved.
 * @returns {Question[]}
 */
export const getShuffledQuestions = (): Question[] => {
    if (!Array.isArray(allQuestions) || allQuestions.length === 0) {
        console.warn("getShuffledQuestions called before questions were loaded or questions list is empty.");
        return [];
    }
    // Фильтруем disabled
    try {
        validateData('questions.schema.json', allQuestions);
    } catch (e) {
        console.error('Ошибка валидации массива вопросов при получении:', e.message);
        return []; // Возвращаем пустой массив, если валидация не пройдена
    }
    return shuffleArray(allQuestions.filter((q: Question) => q && typeof q === 'object' && !q.disabled));
};
